/**
 * Magazine-style itinerary renderer — shared between /plan and /half-days/:slug.
 *
 * Accepts a `result` object from `/api/plan-my-day` or `/api/half-day-itineraries/:slug`.
 * Renders the masthead, narrated opening, numbered stop loop with arrive/depart
 * times + dwell pills, optional closing line, and the standard disclaimer.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Sparkles, Clock, Calendar, Car, Compass, Coffee } from 'lucide-react';

// Stop-time math — durations only, no absolute clocks. We don't know when the
// visitor will set off, so showing "From the off / After 2h 15m" beats "09:30".
function elapsedAtArrival(stops, i) {
  let mins = 0;
  for (let j = 0; j < i; j++) {
    mins += (stops[j].dwell_minutes || 0) + (stops[j + 1]?.drive_minutes_from_prev || 0);
  }
  return mins;
}

function formatDuration(mins) {
  if (mins <= 0) return '0m';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

export default function ItineraryView({ result, mastheadOverride = null }) {
  if (!result) return null;
  if (!result.stops?.length) {
    return (
      <div className="bg-white border border-dashed border-charcoal/15 rounded-3xl p-8 md:p-12 text-center" data-testid="itinerary-empty">
        <Sparkles size={28} className="mx-auto text-muted/40 mb-4"/>
        <h2 className="font-display text-2xl lg:text-3xl mb-3">No stops matched.</h2>
        <p className="text-muted max-w-md mx-auto">{result.tip}</p>
      </div>
    );
  }
  return (
    <article className="bg-white border border-charcoal/10 rounded-[2rem] overflow-hidden shadow-soft" data-testid="itinerary-view">
      {mastheadOverride || (
        <header className="bg-charcoal text-base p-8 md:p-12">
          <div className="flex items-center gap-3 flex-wrap mb-4">
            <span className="label text-mustard">{result.weekday}&apos;s plan</span>
            {result.weather_label && (
              <span className="text-sm bg-white/10 text-base px-3 py-1 rounded-full inline-flex items-center gap-1.5">
                <Compass size={12}/> {result.weather_label}
              </span>
            )}
            <span className="text-sm bg-white/10 text-base px-3 py-1 rounded-full inline-flex items-center gap-1.5">
              <Car size={12}/> {result.total_drive_minutes} min driving · {result.total_miles} mi
            </span>
          </div>
          <h2 className="font-display text-3xl lg:text-5xl leading-[1.05]">
            {result.stops.length} stops, plotted to loop back where you started.
          </h2>
          {result.opening && (
            <p className="text-lg md:text-xl leading-relaxed mt-6 text-base/90 max-w-3xl" data-testid="itinerary-opening">
              {result.opening}
            </p>
          )}
        </header>
      )}

      <ol className="relative">
        {result.stops.map((s, i) => (
          <li key={i} className="grid grid-cols-[2.5rem,1fr] md:grid-cols-[4rem,1fr] gap-0 border-t border-charcoal/10 first:border-t-0" data-testid={`itinerary-stop-${i}`}>
            <div className="flex flex-col items-center pt-8 pb-4">
              <div className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center font-display text-xl font-bold ${s.kind === 'event' ? 'bg-mustard text-charcoal' : 'bg-heritage text-base'}`}>
                {i + 1}
              </div>
              {i < result.stops.length - 1 && (
                <div className="flex-1 w-0.5 bg-charcoal/15 mt-2" aria-hidden />
              )}
            </div>
            <div className="py-8 pr-6 md:pr-12">
              {i > 0 && (
                <div className="label text-charcoal/40 mb-3 inline-flex items-center gap-1.5">
                  <Car size={11}/> {s.drive_minutes_from_prev} min · {s.miles_from_prev} mi from the last stop
                </div>
              )}
              {(s.arrive_time || s.dwell_minutes) && (
                <div className="flex items-center gap-2 mb-3 flex-wrap text-xs">
                  <span className="bg-charcoal text-base px-2.5 py-1 rounded-full inline-flex items-center gap-1.5 font-semibold">
                    <Clock size={11}/>
                    {i === 0 ? 'From the off' : `After ${formatDuration(elapsedAtArrival(result.stops, i))}`}
                  </span>
                  {s.dwell_minutes ? (
                    <span className="bg-stone text-charcoal/70 px-2.5 py-1 rounded-full">
                      ≈ {s.dwell_minutes} min visit
                    </span>
                  ) : null}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-[1fr,auto] gap-6 items-start">
                <div>
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    {s.kind === 'event' && (
                      <span className="bg-mustard/20 text-charcoal text-xs uppercase tracking-wider font-bold px-2 py-1 rounded-md inline-flex items-center gap-1.5">
                        <Calendar size={11}/> On this week
                      </span>
                    )}
                    {s.category === 'food_drink' || (s.category || '').toLowerCase().includes('food') ? (
                      <span className="bg-forest/15 text-forest text-xs uppercase tracking-wider font-bold px-2 py-1 rounded-md inline-flex items-center gap-1.5">
                        <Coffee size={11}/> Food &amp; drink
                      </span>
                    ) : null}
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl leading-tight mb-2">{s.name}</h3>
                  {s.town && (
                    <div className="text-muted text-sm mb-3 inline-flex items-center gap-1.5"><MapPin size={12}/> {s.town}</div>
                  )}
                  <p className="text-charcoal/85 leading-relaxed">{s.blurb}</p>
                  {s.slug && s.kind !== 'event' && (
                    <Link to={`/explore/${s.slug}`} className="mt-4 inline-flex items-center gap-2 font-semibold text-heritage hover:underline">
                      Full details <ArrowRight size={14}/>
                    </Link>
                  )}
                  {s.kind === 'event' && s.url && (
                    <a href={s.url} target="_blank" rel="noreferrer noopener" className="mt-4 inline-flex items-center gap-2 font-semibold text-heritage hover:underline">
                      Book / info <ArrowRight size={14}/>
                    </a>
                  )}
                </div>
                {s.hero_image && (
                  <Link
                    to={s.kind === 'event' ? '#' : `/explore/${s.slug}`}
                    className="block md:w-56 lg:w-64 aspect-[4/3] rounded-2xl overflow-hidden group shrink-0"
                    onClick={(e) => { if (s.kind === 'event') e.preventDefault(); }}
                  >
                    <img src={s.hero_image} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>
                  </Link>
                )}
              </div>
            </div>
          </li>
        ))}
      </ol>

      <footer className="bg-stone p-8 md:p-12 border-t border-charcoal/10">
        {result.closing ? (
          <p className="font-display text-xl md:text-2xl leading-relaxed text-charcoal" data-testid="itinerary-closing">
            {result.closing}
          </p>
        ) : (
          <p className="text-muted italic">Tap any stop above to plan the route in detail.</p>
        )}
        {result.disclaimer && (
          <div
            data-testid="itinerary-disclaimer"
            className="mt-6 pt-6 border-t border-charcoal/10 text-xs text-charcoal/55 italic leading-relaxed max-w-3xl"
          >
            {result.disclaimer}
          </div>
        )}
      </footer>
    </article>
  );
}
